import { useState, useRef } from "react";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { UploadSimple, CheckCircle, Warning, Spinner, XCircle, ArrowRight, Package, FileText, HourglassSimple, SkipForward, FolderOpen } from "@phosphor-icons/react";
import { bulkUploadZip, bulkUploadMultiZip, bulkStartIngestion, bulkGetStatus, classifySinglePdf, formatRound } from "../lib/api";

const CONFIDENCE_STYLES = {
  high: { bg: "bg-[#00C04B]/10", text: "text-[#00C04B]", border: "border-[#00C04B]", label: "HIGH" },
  medium: { bg: "bg-[#FFC107]/10", text: "text-[#92700C]", border: "border-[#FFC107]", label: "MEDIUM" },
  low: { bg: "bg-[#FF2A00]/10", text: "text-[#FF2A00]", border: "border-[#FF2A00]", label: "LOW" },
  unknown: { bg: "bg-[#FF2A00]/10", text: "text-[#FF2A00]", border: "border-[#FF2A00]", label: "NEW STATE" },
  no_parser: { bg: "bg-[#7C3AED]/10", text: "text-[#7C3AED]", border: "border-[#7C3AED]", label: "NO PARSER" },
  duplicate: { bg: "bg-[#FF2A00]/10", text: "text-[#FF2A00]", border: "border-[#FF2A00]", label: "DUPLICATE" },
  skip: { bg: "bg-zinc-100", text: "text-zinc-400", border: "border-zinc-300", label: "SKIP" },
  new_format: { bg: "bg-[#FFC107]/10", text: "text-[#92700C]", border: "border-[#FFC107]", label: "NEW FORMAT" },
};

const STATUS_ICON = {
  pending: <HourglassSimple size={14} className="text-zinc-400" />,
  processing: <Spinner size={14} className="text-[#6043EF] animate-spin" />,
  completed: <CheckCircle size={14} weight="bold" className="text-[#00C04B]" />,
  error: <XCircle size={14} weight="bold" className="text-[#FF2A00]" />,
  skipped: <SkipForward size={14} className="text-zinc-400" />,
};

const KNOWN_BODIES = [
  "MCC", "Karnataka", "Maharashtra", "Kerala", "Telangana_Govt", "Telangana_Mgmt",
  "AP_Govt", "AP_Mgmt", "West_Bengal", "Bihar", "Haryana", "Uttar_Pradesh", "Rajasthan", "Jharkhand",
  "Puducherry", "Madhya_Pradesh",
];

const PDF_TYPES = [
  { value: "allotment", label: "Allotment" },
  { value: "full_seat_matrix", label: "Full Seat Matrix" },
  { value: "provisional_seat_matrix", label: "Provisional SM" },
  { value: "stray_seat_matrix", label: "Stray SM" },
  { value: "merit_list", label: "Merit List" },
];

// Bodies that have a dedicated merit-list parser (backend _dispatch_file). A merit
// list for any other body has no parser yet → still skipped in the batch. Add a
// state here as its merit parser lands (mirrors the backend), opening skip one by one.
const MERIT_PARSER_BODIES = ["Kerala"];
const meritUnsupported = (f) => f.pdf_type === "merit_list" && !MERIT_PARSER_BODIES.includes(f.body);

export default function BulkIngestion() {
  const [mode, setMode] = useState("single"); // "single" or "folder"
  const [year, setYear] = useState("2025");
  const [uploading, setUploading] = useState(false);
  const [batchId, setBatchId] = useState(null);
  const [files, setFiles] = useState([]);
  const [detectedBody, setDetectedBody] = useState(null);
  const [bodiesFound, setBodiesFound] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [batchStatus, setBatchStatus] = useState(null);
  const [error, setError] = useState("");
  const [totalZips, setTotalZips] = useState(0);
  const [sourceUrl, setSourceUrl] = useState("");
  const pdfInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const handleZipUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError("");
    setBatchId(null);
    setFiles([]);
    setBatchStatus(null);
    setTotalZips(0);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("year", year);
      const res = await bulkUploadZip(formData);
      const data = res.data;

      if (data.error) {
        setError(data.error);
      } else {
        setBatchId(data.batch_id);
        setFiles(data.files || []);
        setDetectedBody(data.detected_body);
        setBodiesFound(data.bodies_found || []);
        setTotalZips(1);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Upload failed");
    }
    setUploading(false);
  };

  const handleSinglePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    setBatchId(null);
    setFiles([]);
    setBatchStatus(null);
    setTotalZips(0);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("year", year);
      if (sourceUrl.trim()) formData.append("source_url", sourceUrl.trim());
      const res = await classifySinglePdf(formData);
      const data = res.data;
      if (data.error) {
        setError(data.error);
      } else {
        setBatchId(data.batch_id);
        setFiles(data.files || []);
        setDetectedBody(data.detected_body);
        setBodiesFound(data.bodies_found || []);
        setTotalZips(1);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Upload failed");
    }
    setUploading(false);
  };

  const handleFolderUpload = async (e) => {
    const zipFiles = Array.from(e.target.files || []).filter(f => f.name.toLowerCase().endsWith('.zip'));
    if (zipFiles.length === 0) {
      setError("No ZIP files found in the selected folder");
      return;
    }

    setUploading(true);
    setError("");
    setBatchId(null);
    setFiles([]);
    setBatchStatus(null);
    setTotalZips(0);

    try {
      const formData = new FormData();
      zipFiles.forEach(f => formData.append("files", f));
      formData.append("year", year);
      const res = await bulkUploadMultiZip(formData);
      const data = res.data;

      if (data.error) {
        setError(data.error);
      } else {
        setBatchId(data.batch_id);
        setFiles(data.files || []);
        setDetectedBody(data.detected_body);
        setBodiesFound(data.bodies_found || []);
        setTotalZips(data.total_zips || zipFiles.length);
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Upload failed");
    }
    setUploading(false);
  };

  const updateFile = (idx, field, value) => {
    setFiles(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const ingestableCount = files.filter(f =>
    f.confidence !== "unknown" && f.confidence !== "no_parser" && f.confidence !== "duplicate" && !meritUnsupported(f) && !f._removed
  ).length;

  const handleStartIngestion = async () => {
    if (!batchId) return;
    setProcessing(true);
    setBatchStatus(null);
    setError("");

    try {
      const overrides = files.map(f => ({
        filename: f.filename,
        body: f.body,
        pdf_type: f.pdf_type,
        round_number: f.round_number ? parseInt(f.round_number) : null,
        year: f.year ? parseInt(f.year) : parseInt(year),
        matrix_type: f.matrix_type,
      }));

      await bulkStartIngestion({ batch_id: batchId, files: overrides });

      // Poll for status
      const maxPolls = 1200; // 20 min max
      for (let i = 0; i < maxPolls; i++) {
        try {
          const res = await bulkGetStatus(batchId);
          setBatchStatus(res.data);

          // Update file statuses in local state
          const statusMap = {};
          (res.data.files || []).forEach(f => { statusMap[f.filename] = f; });
          setFiles(prev => prev.map(f => ({
            ...f,
            ingest_status: statusMap[f.filename]?.ingest_status || f.ingest_status,
            ingest_records: statusMap[f.filename]?.ingest_records,
            ingest_colleges: statusMap[f.filename]?.ingest_colleges,
            ingest_note: statusMap[f.filename]?.ingest_note,
          })));

          if (res.data.status === "completed") {
            setProcessing(false);
            return;
          }
        } catch (pe) {
          console.error("Poll error", pe);
        }
        await new Promise(r => setTimeout(r, 1000));
      }
      setError("Batch timed out");
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Batch start failed");
    }
    setProcessing(false);
  };

  const reset = () => {
    setBatchId(null);
    setFiles([]);
    setBatchStatus(null);
    setError("");
    setDetectedBody(null);
    setBodiesFound([]);
    setProcessing(false);
    setTotalZips(0);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (folderInputRef.current) folderInputRef.current.value = "";
  };

  return (
    <div data-testid="bulk-ingestion">
      {/* Upload Section */}
      {!batchId && (
        <div className="border border-zinc-200 border-t-0 p-6" data-testid="bulk-upload-zone">
          {/* Mode Toggle */}
          <div className="flex items-center gap-0 mb-5 border border-zinc-200 w-fit">
            <button
              onClick={() => setMode("folder")}
              className={`h-9 px-5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors ${
                mode === "folder" ? "bg-[#6043EF] text-white" : "bg-white text-[#52525B] hover:bg-zinc-50"
              }`}
              data-testid="mode-folder-btn"
            >
              <FolderOpen size={13} weight="bold" /> FOLDER (MULTI-STATE)
            </button>
            <button
              onClick={() => setMode("single")}
              className={`h-9 px-5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors ${
                mode === "single" ? "bg-[#6043EF] text-white" : "bg-white text-[#52525B] hover:bg-zinc-50"
              }`}
              data-testid="mode-single-btn"
            >
              <Package size={13} weight="bold" /> SINGLE STATE ZIP
            </button>
            <button
              onClick={() => setMode("pdf")}
              className={`h-9 px-5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 transition-colors ${
                mode === "pdf" ? "bg-[#6043EF] text-white" : "bg-white text-[#52525B] hover:bg-zinc-50"
              }`}
              data-testid="mode-pdf-btn"
            >
              <Package size={13} weight="bold" /> SINGLE PDF
            </button>
          </div>

          <p className="text-sm text-[#52525B] font-sans mb-4">
            {mode === "folder"
              ? "Select multiple state ZIP files (Cmd/Ctrl+click). Each ZIP will be auto-classified and merged into one batch."
              : mode === "pdf"
              ? "Upload a single allotment / seat-matrix / merit-list PDF (or XLSX). It runs through the same pipeline as bulk — dedicated parser, Firebase copy, and a full Ingestion Diary entry with a Re-ingest button."
              : "Upload a ZIP containing all PDFs (allotments + seat matrices + merit/rank lists) for a state and year."
            }
          </p>
          <div className="flex items-end gap-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 block">YEAR</label>
              <Input
                type="number" value={year} onChange={e => setYear(e.target.value)}
                className="w-24 border-zinc-300 font-['IBM_Plex_Mono'] focus:border-[#6043EF]"
                data-testid="bulk-year-input"
              />
            </div>
            <div className="flex-1">
              {mode === "single" ? (
                <>
                  <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 block">ZIP FILE</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".zip"
                    onChange={handleZipUpload}
                    className="block w-full text-sm text-[#52525B] font-sans
                      file:mr-3 file:py-2 file:px-4 file:border-0 file:text-xs file:font-bold
                      file:uppercase file:tracking-wider file:bg-[#6043EF] file:text-white
                      file:cursor-pointer file:transition-colors file:hover:bg-[#4A28C2]"
                    data-testid="bulk-zip-input"
                  />
                </>
              ) : mode === "pdf" ? (
                <>
                  <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 block">PDF / XLSX FILE</label>
                  <input
                    ref={pdfInputRef}
                    type="file"
                    accept=".pdf,.xlsx,.xls"
                    onChange={handleSinglePdfUpload}
                    className="block w-full text-sm text-[#52525B] font-sans
                      file:mr-3 file:py-2 file:px-4 file:border-0 file:text-xs file:font-bold
                      file:uppercase file:tracking-wider file:bg-[#6043EF] file:text-white
                      file:cursor-pointer file:transition-colors file:hover:bg-[#4A28C2]"
                    data-testid="single-pdf-input"
                  />
                </>
              ) : (
                <>
                  <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 block">SELECT STATE ZIPS</label>
                  <input
                    ref={folderInputRef}
                    type="file"
                    accept=".zip"
                    multiple
                    onChange={handleFolderUpload}
                    className="block w-full text-sm text-[#52525B] font-sans
                      file:mr-3 file:py-2 file:px-4 file:border-0 file:text-xs file:font-bold
                      file:uppercase file:tracking-wider file:bg-[#6043EF] file:text-white
                      file:cursor-pointer file:transition-colors file:hover:bg-[#4A28C2]"
                    data-testid="bulk-folder-input"
                  />
                </>
              )}
            </div>
            {uploading && (
              <div className="flex items-center gap-2 text-xs text-[#6043EF] font-bold">
                <Spinner size={14} className="animate-spin" /> CLASSIFYING...
              </div>
            )}
          </div>
          {mode === "pdf" && (
            <div className="mt-3">
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5 block">SOURCE URL <span className="text-zinc-400 normal-case tracking-normal font-normal">(optional — the page/PDF this file came from)</span></label>
              <Input
                type="url" value={sourceUrl} onChange={e => setSourceUrl(e.target.value)}
                placeholder="https://…"
                className="w-full border-zinc-300 font-sans focus:border-[#6043EF]"
                data-testid="single-source-url-input"
              />
            </div>
          )}
          {error && (
            <div className="mt-3 p-3 border border-[#FF2A00] bg-[#FF2A00]/5 text-sm text-[#FF2A00]" data-testid="bulk-error">
              {error}
            </div>
          )}
        </div>
      )}

      {/* Classification Review */}
      {batchId && files.length > 0 && (
        <div className="border border-zinc-200 border-t-0" data-testid="bulk-classification">
          {/* Summary Bar */}
          <div className="p-4 bg-[#F4F4F5] border-b border-zinc-200 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">
                {files.length} FILES CLASSIFIED
                {totalZips > 1 && <span className="font-normal text-[#52525B]"> from {totalZips} ZIPs</span>}
              </span>
              {detectedBody && (
                <Badge className="bg-[#6043EF] text-white text-[10px] uppercase tracking-wider">{detectedBody}</Badge>
              )}
              {!detectedBody && bodiesFound.length > 0 && bodiesFound.map(b => (
                <Badge key={b} className="bg-[#6043EF] text-white text-[10px] uppercase tracking-wider">{b}</Badge>
              ))}
              <span className="font-['IBM_Plex_Mono'] text-xs text-[#52525B]">
                {ingestableCount} ingestable
              </span>
              {files.filter(f => f.is_duplicate).length > 0 && (
                <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-[#FF2A00] text-[#FF2A00]">
                  {files.filter(f => f.is_duplicate).length} DUPLICATE
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!processing && !batchStatus?.status?.includes("completed") && (
                <button
                  onClick={handleStartIngestion}
                  disabled={ingestableCount === 0}
                  className="h-8 px-4 bg-[#6043EF] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#4A28C2] disabled:opacity-50 flex items-center gap-1.5 transition-colors"
                  data-testid="bulk-start-btn"
                >
                  <ArrowRight size={12} weight="bold" /> INGEST ALL ({ingestableCount})
                </button>
              )}
              <button
                onClick={reset}
                className="h-8 px-4 border border-zinc-300 text-[#52525B] text-[10px] font-bold uppercase tracking-wider hover:bg-zinc-50 transition-colors"
                data-testid="bulk-reset-btn"
              >
                RESET
              </button>
            </div>
          </div>

          {/* Progress Bar */}
          {processing && batchStatus && (
            <div className="p-3 bg-[#6043EF]/5 border-b border-zinc-200" data-testid="bulk-progress">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#6043EF]">
                  PROCESSING {batchStatus.current_file || "..."}
                </span>
                <span className="font-['IBM_Plex_Mono'] text-xs text-[#52525B]">
                  {(batchStatus.current_file_index || 0) + 1} / {batchStatus.processable_count || 0}
                </span>
              </div>
              <div className="w-full h-1.5 bg-zinc-200">
                <div
                  className="h-full bg-[#6043EF] transition-all duration-300"
                  style={{ width: `${batchStatus.processable_count ? (((batchStatus.current_file_index || 0) + 1) / batchStatus.processable_count) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          {/* Completion Summary */}
          {batchStatus?.status === "completed" && (
            <div className="p-3 bg-[#00C04B]/5 border-b border-zinc-200 flex items-center gap-3" data-testid="bulk-complete">
              <CheckCircle size={16} weight="bold" className="text-[#00C04B]" />
              <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#00C04B]">BATCH COMPLETE</span>
              <span className="font-['IBM_Plex_Mono'] text-sm text-[#09090B]">
                {batchStatus.total_records?.toLocaleString()} records
              </span>
              <span className="font-['IBM_Plex_Mono'] text-sm text-[#52525B]">
                {batchStatus.total_colleges} colleges
              </span>
              {batchStatus.failed_count > 0 && (
                <Badge className="bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00] text-[10px]">
                  {batchStatus.failed_count} FAILED
                </Badge>
              )}
            </div>
          )}

          {/* Files Table — grouped by source_zip when multi-zip, flat otherwise */}
          {(() => {
            const isMulti = totalZips > 1;
            // Group files by source_zip for multi-zip mode
            const groups = isMulti
              ? files.reduce((acc, f, idx) => {
                  const key = f.source_zip || "Unknown";
                  if (!acc[key]) acc[key] = [];
                  acc[key].push({ ...f, _globalIdx: idx });
                  return acc;
                }, {})
              : { "": files.map((f, idx) => ({ ...f, _globalIdx: idx })) };

            const renderFileRow = (f, i) => {
              const isSkipType = f.confidence === "no_parser" || f.confidence === "unknown" || meritUnsupported(f);
              const confKey = f.is_duplicate ? "duplicate" : (meritUnsupported(f) ? "skip" : f.confidence);
              const conf = CONFIDENCE_STYLES[confKey] || CONFIDENCE_STYLES.unknown;
              const isEditable = !processing && batchStatus?.status !== "completed";
              const rowBg = f.is_duplicate ? "bg-[#FF2A00]/5" : isSkipType ? "bg-zinc-50 opacity-60" : "";
              return (
                <TableRow key={`${f.source_zip || ""}_${f.filename}`} className={`hover:bg-zinc-50 ${rowBg}`} data-testid={`bulk-file-row-${f._globalIdx}`}>
                  <TableCell className="font-['IBM_Plex_Mono'] text-xs text-zinc-400">{i + 1}</TableCell>
                  <TableCell>
                    {isEditable ? (
                      <button
                        onClick={() => removeFile(f._globalIdx)}
                        className="w-6 h-6 flex items-center justify-center text-zinc-400 hover:text-[#FF2A00] hover:bg-[#FF2A00]/10 transition-colors"
                        title="Remove this file from batch"
                        data-testid={`bulk-remove-${f._globalIdx}`}
                      >
                        <XCircle size={14} weight="bold" />
                      </button>
                    ) : (
                      STATUS_ICON[f.ingest_status || "pending"]
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <FileText size={14} className="text-zinc-400 shrink-0" />
                      <span className="text-xs font-medium truncate max-w-[220px]" title={f.filename}>{f.filename}</span>
                      {f._course_scope === "BDS" && (
                        <span className="text-[9px] font-bold px-1 py-0.5 bg-[#7C3AED]/10 text-[#7C3AED] border border-[#7C3AED]/30 shrink-0">BDS</span>
                      )}
                    </div>
                    {f.is_duplicate && (
                      <span className="text-[10px] block mt-0.5 text-[#FF2A00] font-bold">
                        DUPLICATE — same {f._course_scope || ""} data as "{f.duplicate_of}"
                      </span>
                    )}
                    {!f.is_duplicate && f.pdf_type === "merit_list" && !f.ingest_note && (
                      <span className={`text-[10px] block mt-0.5 ${meritUnsupported(f) ? "text-zinc-400 font-bold" : "text-zinc-400"}`}>
                        {meritUnsupported(f)
                          ? `SKIP — no merit-list parser for ${f.body || "this body"} yet`
                          : "Merit/rank list → supplies true NEET AIR (enriches this state's allotments)"}
                      </span>
                    )}
                    {f.ingest_note && (
                      <span className={`text-[10px] block mt-0.5 ${f.ingest_status === "error" ? "text-[#FF2A00]" : "text-zinc-400"}`}>
                        {f.ingest_note}
                      </span>
                    )}
                    {!f.ingest_note && !f.is_duplicate && f.pdf_type !== "merit_list" && f.note && (
                      <span className="text-[10px] block mt-0.5 text-zinc-400">{f.note}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditable ? (
                      <Select value={f.body || "unknown"} onValueChange={v => updateFile(f._globalIdx, "body", v === "unknown" ? null : v)}>
                        <SelectTrigger className="h-7 w-36 text-[10px] border-zinc-300" data-testid={`bulk-body-${f._globalIdx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unknown">Unknown</SelectItem>
                          {KNOWN_BODIES.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs">{f.body || "—"}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditable ? (
                      <Select value={f.pdf_type || "unknown"} onValueChange={v => updateFile(f._globalIdx, "pdf_type", v === "unknown" ? null : v)}>
                        <SelectTrigger className="h-7 w-32 text-[10px] border-zinc-300" data-testid={`bulk-type-${f._globalIdx}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unknown">Unknown</SelectItem>
                          {PDF_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider">{f.pdf_type || "—"}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditable ? (
                      <Input
                        type="number"
                        value={f.round_number || ""}
                        onChange={e => updateFile(f._globalIdx, "round_number", e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="—"
                        className="h-7 w-16 text-[10px] font-['IBM_Plex_Mono'] border-zinc-300"
                        data-testid={`bulk-round-${f._globalIdx}`}
                      />
                    ) : (
                      <span className="font-['IBM_Plex_Mono'] text-xs">{f.round_number != null ? formatRound(f.round_number) : "—"}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditable ? (
                      <Input
                        type="number"
                        value={f.year || ""}
                        onChange={e => updateFile(f._globalIdx, "year", e.target.value ? parseInt(e.target.value) : null)}
                        className="h-7 w-16 text-[10px] font-['IBM_Plex_Mono'] border-zinc-300"
                        data-testid={`bulk-year-${f._globalIdx}`}
                      />
                    ) : (
                      <span className="font-['IBM_Plex_Mono'] text-xs">{f.year || "—"}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider ${conf.bg} ${conf.text} ${conf.border}`}>
                      {conf.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {f.ingest_status === "completed" && (
                      <span className={`font-['IBM_Plex_Mono'] text-xs font-bold ${f.ingest_records ? "text-[#00C04B]" : "text-[#FF2A00]"}`}>
                        {f.ingest_records?.toLocaleString() || 0} rec
                      </span>
                    )}
                    {f.ingest_status === "error" && (
                      <span className="text-[10px] text-[#FF2A00] font-bold">FAILED</span>
                    )}
                    {f.ingest_status === "skipped" && (
                      <span className="text-[10px] text-zinc-400">SKIP</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            };

            const tableHeader = (
              <TableHeader>
                <TableRow className="bg-[#F4F4F5] hover:bg-[#F4F4F5]">
                  <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] w-6">#</TableHead>
                  <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">
                    {processing || batchStatus?.status === "completed" ? "STATUS" : ""}
                  </TableHead>
                  <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] min-w-[200px]">FILE</TableHead>
                  <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">BODY</TableHead>
                  <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">TYPE</TableHead>
                  <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">ROUND</TableHead>
                  <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">YEAR</TableHead>
                  <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">CONFIDENCE</TableHead>
                  <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right">RESULT</TableHead>
                </TableRow>
              </TableHeader>
            );

            return Object.entries(groups).map(([zipName, groupFiles]) => {
              const groupBodies = [...new Set(groupFiles.map(f => f.body).filter(Boolean))];
              const groupIngestable = groupFiles.filter(f =>
                f.confidence !== "unknown" && f.confidence !== "no_parser" && f.confidence !== "duplicate" && !meritUnsupported(f)
              ).length;
              const groupRecords = groupFiles.reduce((sum, f) => sum + (f.ingest_records || 0), 0);

              return (
                <div key={zipName} className="overflow-x-auto" data-testid={`bulk-group-${zipName}`}>
                  {isMulti && (
                    <div className="p-3 bg-[#09090B] flex items-center gap-3">
                      <Package size={12} className="text-white/60" />
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white">{zipName}</span>
                      {groupBodies.map(b => (
                        <Badge key={b} className="bg-[#6043EF] text-white text-[9px] uppercase tracking-wider">{b}</Badge>
                      ))}
                      <span className="font-['IBM_Plex_Mono'] text-[10px] text-white/60 ml-auto">
                        {groupFiles.length} files, {groupIngestable} ingestable
                        {groupRecords > 0 && ` — ${groupRecords.toLocaleString()} rec`}
                      </span>
                    </div>
                  )}
                  <Table>
                    {tableHeader}
                    <TableBody>
                      {groupFiles.map((f, i) => renderFileRow(f, i))}
                    </TableBody>
                  </Table>
                </div>
              );
            });
          })()}

          {/* Round number legend */}
          <div className="px-4 py-3 border border-zinc-200 border-t-0 bg-white">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#52525B] mb-1.5">ROUND NUMBER CONVENTION</p>
            <div className="flex flex-wrap gap-x-6 gap-y-0.5 text-[10px] font-['IBM_Plex_Mono'] text-[#52525B]">
              <span><b className="text-[#09090B]">1–7</b> Regular rounds</span>
              <span><b className="text-[#09090B]">50</b> Full seat matrix</span>
              <span><b className="text-[#09090B]">51–57</b> Provisional SM (R1–R7)</span>
              <span><b className="text-[#09090B]">101–107</b> Stray rounds</span>
              <span><b className="text-[#09090B]">150</b> Stray seat matrix</span>
              <span><b className="text-[#09090B]">201+</b> Special stray</span>
              <span><b className="text-[#09090B]">250</b> Special stray SM</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
